// frontend/src/utils/formatters.ts
// 🔧 CENTRALIZED NUMBER FORMATTING UTILITIES
// Use these throughout the app for consistent, readable number displays

/**
 * Format number with commas for readability
 * Example: 27395980 → "27,395,980"
 */
export const formatNumberWithCommas = (num: number | string | undefined | null): string => {
  if (num === undefined || num === null || num === '') return '0';
  
  const numValue = typeof num === 'string' ? parseFloat(num.replace(/,/g, '')) : num;
  
  if (isNaN(numValue)) return '0';
  
  // Format with commas
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

/**
 * Format currency with appropriate suffix (K, M, B)
 * Example: 1500000 → "$1.5M"
 */
export const formatCurrency = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === '') return '$0';
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(numValue) || numValue === 0) return '$0';
  
  if (numValue >= 1e9) {
    return `$${(numValue / 1e9).toFixed(2)}B`;
  }
  if (numValue >= 1e6) {
    return `$${(numValue / 1e6).toFixed(2)}M`;
  }
  if (numValue >= 1e3) {
    return `$${(numValue / 1e3).toFixed(0)}K`;
  }
  
  return `$${formatNumberWithCommas(numValue)}`;
};

/**
 * Format currency with full commas (no abbreviation)
 * Example: 1500000 → "$1,500,000"
 */
export const formatCurrencyFull = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === '') return '$0';
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(numValue)) return '$0';
  
  return `$${formatNumberWithCommas(numValue)}`;
};

/**
 * Format large numbers with appropriate suffix (K, M, B, T)
 * Example: 27395980 → "27.4M"
 */
export const formatLargeNumber = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === '') return '0';
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(numValue)) return '0';
  
  if (numValue >= 1e12) {
    return `${(numValue / 1e12).toFixed(2)}T`;
  }
  if (numValue >= 1e9) {
    return `${(numValue / 1e9).toFixed(2)}B`;
  }
  if (numValue >= 1e6) {
    return `${(numValue / 1e6).toFixed(2)}M`;
  }
  if (numValue >= 1e3) {
    return `${(numValue / 1e3).toFixed(0)}K`;
  }
  
  return formatNumberWithCommas(numValue);
};

/**
 * Format percentage
 * Example: 0.1234 → "12.34%"
 */
export const formatPercentage = (value: number | string | undefined | null, decimals: number = 2): string => {
  if (value === undefined || value === null || value === '') return '0%';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return '0%';
  
  return `${(numValue * 100).toFixed(decimals)}%`;
};

/**
 * Format decimal number to fixed decimals
 * Example: 5.6789 → "5.68"
 */
export const formatDecimal = (value: number | string | undefined | null, decimals: number = 2): string => {
  if (value === undefined || value === null || value === '') return '0';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return '0';
  
  return numValue.toFixed(decimals);
};

/**
 * Format weight/mass (lbs, kg, tons)
 * Example: 27395980 → "27,395,980 lbs"
 */
export const formatWeight = (
  value: number | string | undefined | null, 
  unit: 'lbs' | 'kg' | 'tons' = 'lbs',
  useAbbreviation: boolean = false
): string => {
  if (value === undefined || value === null || value === '') return `0 ${unit}`;
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(numValue)) return `0 ${unit}`;
  
  if (useAbbreviation) {
    if (numValue >= 1e9) {
      return `${(numValue / 1e9).toFixed(2)}B ${unit}`;
    }
    if (numValue >= 1e6) {
      return `${(numValue / 1e6).toFixed(2)}M ${unit}`;
    }
    if (numValue >= 1e3) {
      return `${(numValue / 1e3).toFixed(0)}K ${unit}`;
    }
  }
  
  return `${formatNumberWithCommas(numValue)} ${unit}`;
};

/**
 * Format area (sq ft, acres)
 * Example: 5000 → "5,000 sq ft"
 */
export const formatArea = (
  value: number | string | undefined | null, 
  unit: 'sq ft' | 'acres' | 'sq m' = 'sq ft'
): string => {
  if (value === undefined || value === null || value === '') return `0 ${unit}`;
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  
  if (isNaN(numValue)) return `0 ${unit}`;
  
  return `${formatNumberWithCommas(numValue)} ${unit}`;
};

/**
 * Format time duration (years, months, days)
 * Example: 365 → "1 year"
 */
export const formatDuration = (days: number | undefined | null): string => {
  if (days === undefined || days === null) return '0 days';
  
  if (days >= 365) {
    const years = Math.floor(days / 365);
    return `${years} year${years > 1 ? 's' : ''}`;
  }
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? 's' : ''}`;
  }
  return `${days} day${days > 1 ? 's' : ''}`;
};

/**
 * Safely convert any value to a number
 * Returns 0 if conversion fails
 */
export const toNumber = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[^0-9.-]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// ============================================================================
// NEW: RISK SCORING FUNCTIONS (1-10 scale)
// These functions work with the standard 1-10 risk scale used in climate_risk
// and city_climate_risk tables
// ============================================================================

/**
 * Get risk color based on 1-10 risk score
 * Used for consistent color coding across the application
 * Example: 8.5 → "#d32f2f" (red)
 */
export const getRiskColor = (risk: number | undefined | null): string => {
  if (risk === undefined || risk === null || isNaN(risk)) return '#9e9e9e'; // Gray for unknown
  
  if (risk > 7) return '#d32f2f'; // Very High (red)
  if (risk > 6) return '#f57c00'; // High (orange)
  if (risk > 3) return '#fbc02d'; // Medium (yellow)
  return '#4caf50'; // Low (green)
};

/**
 * Get risk label based on 1-10 risk score
 * Example: 8.5 → "Very High"
 */
export const getRiskLabel = (risk: number | undefined | null): string => {
  if (risk === undefined || risk === null || isNaN(risk)) return 'Unknown';
  
  if (risk > 7) return 'Very High';
  if (risk > 6) return 'High';
  if (risk > 3) return 'Medium';
  return 'Low';
};

/**
 * Get background color for risk score (lighter version for cards/backgrounds)
 * Example: 8.5 → "#ffebee" (light red background)
 */
export const getRiskBackgroundColor = (risk: number | undefined | null): string => {
  if (risk === undefined || risk === null || isNaN(risk)) return '#f5f5f5'; // Light gray
  
  if (risk > 7) return '#ffebee'; // Light red
  if (risk > 6) return '#fff3e0'; // Light orange
  if (risk > 3) return '#fffde7'; // Light yellow
  return '#e8f5e9'; // Light green
};

/**
 * Get risk category icon name (for Material-UI icons)
 * Example: 8.5 → "error" (maps to ErrorIcon)
 */
export const getRiskIcon = (risk: number | undefined | null): string => {
  if (risk === undefined || risk === null || isNaN(risk)) return 'help';
  
  if (risk > 7) return 'error'; // ErrorIcon
  if (risk > 6) return 'warning'; // WarningIcon
  if (risk > 3) return 'info'; // InfoIcon
  return 'check_circle'; // CheckCircleIcon
};

/**
 * Format risk score with label
 * Example: 8.5 → "8.5 (Very High)"
 */
export const formatRiskScore = (risk: number | undefined | null): string => {
  if (risk === undefined || risk === null || isNaN(risk)) return 'N/A';
  
  return `${risk.toFixed(1)} (${getRiskLabel(risk)})`;
};

// Export all formatters
export default {
  formatNumberWithCommas,
  formatCurrency,
  formatCurrencyFull,
  formatLargeNumber,
  formatPercentage,
  formatDecimal,
  formatWeight,
  formatArea,
  formatDuration,
  toNumber,
  getRiskColor,
  getRiskLabel,
  getRiskBackgroundColor,
  getRiskIcon,
  formatRiskScore,
};